import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CustomImage,
  GUEST_UPLOAD_NOT_ALLOWED_ERROR,
  MAX_CUSTOM_BACKGROUNDS,
  MAX_CUSTOM_BACKGROUNDS_REACHED_ERROR,
} from 'contexts/CustomImagesContext/CustomImagesProvider';
import { VideoEffectOptions } from 'contexts/LiveKitDevicesStateContext/LiveKitDevicesStateContext';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import {
  getStoredImagesFromMatrix,
  ImageDimensions,
  removeImageReferenceFromMatrix,
  uploadAndStoreImageInMatrix,
} from 'modules/MediaDevicesSettings/VisualEffectsSettings/utils/matrixImageStorage';
import logger from 'utils/logging/faro';

import { useBlobUrlManagement } from './useBlobUrlManagement';
import { useCustomImagesSync } from './useCustomImagesSync';
import {
  SELECTED_CUSTOM_IMAGE_ID_KEY,
  createCustomImageFromMatrix,
  fetchCustomImagePathFromMatrix,
  findCustomImageById,
  getSelectedImageIdFromStorage,
} from './utils';

const useCustomImages = () => {
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { mxLoggedIn } = useMxCookies();
  const [customImages, setCustomImages] = useState<CustomImage[]>([]);
  const [selectedCustomImageId, setSelectedCustomImageId] = useLocalStorage<string | null>(
    SELECTED_CUSTOM_IMAGE_ID_KEY,
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  // Blob URL management
  const { blobUrlsRef, cleanupAllBlobUrls, cleanupUnusedBlobUrlsFn } = useBlobUrlManagement();

  // Sync images from Matrix account data
  useCustomImagesSync(matrixClient, blobUrlsRef, cleanupUnusedBlobUrlsFn, setCustomImages);

  const addCustomImage = async (file: File, hash?: string, dimensions?: ImageDimensions): Promise<string> => {
    // Only logged-in users can upload custom backgrounds
    if (!mxLoggedIn) throw new Error(GUEST_UPLOAD_NOT_ALLOWED_ERROR);
    if (!matrixClient) throw new Error('Matrix client not available');

    // Check Matrix storage directly for the current count before uploading
    const storedImages = getStoredImagesFromMatrix(matrixClient);
    if (storedImages.length >= MAX_CUSTOM_BACKGROUNDS) throw new Error(MAX_CUSTOM_BACKGROUNDS_REACHED_ERROR);

    setIsLoading(true);
    try {
      // Upload and store in Matrix
      const { id } = await uploadAndStoreImageInMatrix(matrixClient, file, hash, dimensions);

      // Note: State will be updated automatically by the AccountData event listener
      // No need to manually update state here to avoid duplicate additions

      return id;
    } catch (error) {
      logger.error('Error adding custom image', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removeCustomImage = async (id: string) => {
    // Only logged-in users can remove custom backgrounds
    if (!mxLoggedIn) throw new Error(GUEST_UPLOAD_NOT_ALLOWED_ERROR);
    if (!matrixClient) throw new Error('Matrix client not available');

    try {
      // Remove from Matrix account data
      // Blob URL cleanup will be handled automatically by cleanupUnusedBlobUrls
      // when the AccountData event triggers a reload
      await removeImageReferenceFromMatrix(matrixClient, id);

      // Note: State will be updated automatically by the AccountData event listener
      // Blob URLs will be properly cleaned up without double-revoke

      if (selectedCustomImageId === id) setSelectedCustomImageId(null);
    } catch (error) {
      logger.error('Error removing custom image', error);
      throw error;
    }
  };

  const getCustomImageById = useCallback((id: string) => findCustomImageById(customImages, id), [customImages]);

  const getCustomImagePathById = useCallback(
    async (id: string): Promise<string | null> => {
      // First try to find in React state
      const image = getCustomImageById(id);
      if (image?.path) return image.path;

      // If not found in React state, fetch from Matrix account data
      // This handles the case where image was just uploaded and React state hasn't updated yet
      if (!matrixClient) return null;

      try {
        const blobUrl = await fetchCustomImagePathFromMatrix(matrixClient, id, blobUrlsRef);
        if (!blobUrl) return null;

        // Get the matrix image to create CustomImage object
        const matrixImages = getStoredImagesFromMatrix(matrixClient);
        const matrixImage = matrixImages.find((img) => img.id === id);
        if (!matrixImage) return null;

        // Update state so it's available next time
        const customImage = createCustomImageFromMatrix(matrixImage, blobUrl);
        setCustomImages((prev) => {
          // Only add if not already present
          if (prev.find((img) => img.id === id)) return prev;
          return [...prev, customImage];
        });
        return blobUrl;
      } catch (error) {
        logger.warn('Error fetching image from Matrix in getCustomImagePathById', error);
        return null;
      }
    },
    [matrixClient, getCustomImageById, blobUrlsRef]
  );

  const getCustomImagePathForEffect = useCallback(
    async (effect: VideoEffectOptions, currentPath: string | null | undefined): Promise<string | null | undefined> => {
      if (effect !== VideoEffectOptions.CUSTOM_IMAGE || currentPath) return currentPath;

      const parsedId = getSelectedImageIdFromStorage();
      if (parsedId) return await getCustomImagePathById(parsedId);

      return;
    },
    [getCustomImagePathById]
  );

  const getSelectedCustomImagePath = useCallback((): string | null => {
    // Read selectedCustomImageId directly from localStorage to get the latest value
    // This works reliably because setSelectedCustomImageId (via useLocalStorage)
    // writes to localStorage SYNCHRONOUSLY before updating React state
    const storedId = getSelectedImageIdFromStorage();
    const currentSelectedId = storedId || selectedCustomImageId;

    if (!currentSelectedId) return null;

    // Find the image in the customImages array
    const image = getCustomImageById(currentSelectedId);
    if (!image) return null;

    // Return the blob URL (fetched from Matrix with authentication)
    return image.path || null;
  }, [selectedCustomImageId, getCustomImageById]);

  const customImageOptions = useMemo(
    () =>
      customImages.map(({ path, id }) => ({
        id: VideoEffectOptions.CUSTOM_IMAGE,
        customImageId: id,
        uniqueKey: id,
        path,
      })),
    [customImages]
  );

  const canUploadMore = customImages.length < MAX_CUSTOM_BACKGROUNDS;
  const remainingSlots = MAX_CUSTOM_BACKGROUNDS - customImages.length;

  // Clean up all blob URLs on component unmount
  useEffect(() => cleanupAllBlobUrls, [cleanupAllBlobUrls]);

  return {
    customImages,
    customImageOptions,
    selectedCustomImageId,
    setSelectedCustomImageId,
    addCustomImage,
    removeCustomImage,
    getCustomImageById,
    getSelectedCustomImagePath,
    getCustomImagePathById,
    getCustomImagePathForEffect,
    isLoading,
    canUploadMore,
    remainingSlots,
    maxCustomBackgrounds: MAX_CUSTOM_BACKGROUNDS,
  };
};

export default useCustomImages;
